import {
	SidebarGroup,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar
} from '@repo/ui/sidebar'
import { Link, type LinkProps } from '@tanstack/react-router'
import { Building2, ScrollText, UserCog } from 'lucide-react'
import type { ReactElement } from 'react'
import { useTranslation } from '@/lib/i18n'

interface SidebarLink {
	icon: ReactElement
	label: string
	to: LinkProps['to']
	/**
	 * When `true`, the link is only active when the URL matches `to` exactly.
	 * Defaults to `false` — child routes keep the parent link active (e.g.
	 * "/organizations/123" highlights "Organizations").
	 */
	exact?: boolean
}

/**
 * The platform operator console's navigation, on the topology TD.3 ratified.
 *
 * WHAT LEFT, AND WHY IT IS NOT A REGRESSION. This list held a second entry,
 * `/users`, a flat instance-wide user table inherited from the
 * starter. The product has no such surface: every operator flow reaches a
 * person through their organization, and force-disconnect lives on that
 * organization's sessions page. The route went with the entry, so navigation
 * and route tree still agree — the criterion is "no route without a surface",
 * and a surface the design does not contain cannot acquire one here.
 *
 * A LINK EXISTS WHEN ITS ROUTE DOES. `to` is typed against the generated route
 * tree, so an entry cannot be written ahead of its page. The full design and
 * the milestone that brings each surface:
 *
 *   Organizzazioni   Elenco e usage     `/organizations`      MS6
 *                    Nuova              `/organizations/new`  MS6
 *   Audit log        Registro operatore `/audit`              MS10
 *   Operatori        Inviti e accessi   `/operators`          10/2026
 *   Istanza          Salute e capacità  `/health`             MS12
 *   Personale        Profilo            `/settings`           MS9
 *                    Sicurezza          `/settings`           MS9
 *
 * Suspension, deletion, first-admin reassignment and force-disconnect are
 * actions on one organization, not menu entries: they live in the detail page
 * and its danger zone. Creation is no entry either, although the design calls
 * it the channel model's central onboarding flow: it is the organizations
 * list's primary action, on the trailing edge of that list's toolbar, and
 * «Nuova» above stays a dialog opened from there rather than a route.
 *
 * Three things that look like menu entries and are not. Usage is a column of
 * the organizations list. Brand configuration is a step of instance bootstrap
 * and lives outside the web runtime. Email templates are branded surface, but
 * the web app does not serve them.
 */
function OperatorNavGroups() {
	const { t } = useTranslation()
	const { isMobile, setOpenMobile } = useSidebar()

	const handleNavigate = () => {
		if (isMobile) {
			setOpenMobile(false)
		}
	}

	// Every link is visible without a per-entry role check, and here that is
	// correct rather than a gap: this whole artifact serves one role, and the
	// operator area guard refuses anyone else above every one of these routes.
	// A per-link check would repeat that single condition once per entry.
	// Three entries. The platform console that used to sit at the area's root
	// is gone — it held one card, a shortcut into this same
	// organizations list, and creation became that list's own toolbar
	// action (`CreateOrganizationAction`) rather than a link of its own. The other absent entry is `/users`
	// — every user of every customer, with ban, unban and deletion attached.
	// OPSSTD-5305 removed it: the operator administers organizations and
	// reaches a person through the one that holds them, so nothing in this
	// navigation may list people across customers .
	const links: SidebarLink[] = [
		{
			icon: <Building2 />,
			label: t('operator-organizations:page_title'),
			to: '/organizations'
		},
		{
			icon: <ScrollText />,
			label: t('operator-audit:page_title'),
			to: '/audit'
		},
		// The console's own operators — not people of any customer, so it
		// does not reopen the cross-tenant user table OPSSTD-5305 removed.
		{
			icon: <UserCog />,
			label: t('operator-operators:page_title'),
			to: '/operators'
		}
	]

	return (
		<SidebarGroup>
			<SidebarMenu>
				{links.map((link) => (
					<SidebarMenuItem key={link.label}>
						<SidebarMenuButton
							className='data-[status=active]:bg-sidebar-accent data-[status=active]:font-medium data-[status=active]:text-sidebar-accent-foreground'
							render={
								<Link
									activeOptions={{
										exact: link.exact ?? false
									}}
									onClick={handleNavigate}
									to={link.to}
								/>
							}
							tooltip={link.label}
						>
							{link.icon}
							<span>{link.label}</span>
						</SidebarMenuButton>
					</SidebarMenuItem>
				))}
			</SidebarMenu>
		</SidebarGroup>
	)
}

export { OperatorNavGroups }
